import { DataScienceEnums } from 'dashboard-services/api/dataScience';
import { CommonService, ReduxService } from 'dashboard-services/services';

import { basicPipelineCreatorActions } from 'actions/modules/datascience/creator';

import { CommentNodeModel } from './Diagram/CommentNode';

import { Point } from '@projectstorm/geometry';

export default (() => {

  const getTasksNameInPipeline = ({ pipelineTasks, readableName, definitionName }) => {
    const base = String(readableName).startsWith(definitionName) ? definitionName : readableName
    let newName = base,
        count = 0;
    while(Object.values(pipelineTasks).findIndex(pt => pt.readableName === newName) > -1) {
      count++;
      newName = `${base}(${count})`
    }
    return newName;
  }

  const getTask = ({ taskId, tasks, searchResults, categoryName, groupName, name, pipelineTasks }) => {
    const arrayToSearch = (searchResults || []).concat(tasks[categoryName] || [])
    const found = arrayToSearch.find(t => t.id === taskId) || arrayToSearch.find(t => t.groupName === groupName && t.name === name)
    const readableName = getTasksNameInPipeline({ pipelineTasks, readableName: found?.readableName, definitionName: found?.definitionName || found?.readableName })
    return found ? ReduxService.updateObject(found, { 
      definitionName: found.readableName, 
      definitionId: found.id,
      readableName
    }) : undefined
  }
  
  const getCategory = (categoryName, categories) => categories.find(c => c.name === categoryName) || categories.find(c => c.name === "Other") 
  
  const removeDuplicates = arr => {
    const newArr = [];
    (arr || []).forEach(item => {
      const finded = newArr.find(i => i.input.task === item.input.task && i.input.name === item.input.name && i.output.task === item.output.task && i.output.name === item.output.name)
      if(!finded) {
        newArr.push(item)
      }
    })
    return newArr;
  }
  
  const getLinkObj = (inputTask, inputName, outputTask, outputName, value) => ({
    value,
    output: {
      task: outputTask,
      name: outputName
    },
    input: {
      task: inputTask,
      name: inputName
    }
  })
  
  const resolveValue = (value, defaultValue) => {
    if(value !== undefined && value !== "") {
      return CommonService.isJson(value) ? JSON.parse(value) : value
    }
    return defaultValue
  }
  
  const getTaskDef = (taskDefinitions, task, { cleanDynamicValue = true, ignoreVersion = false } = {}) => {
    const taskDef = ReduxService.cloneObject(taskDefinitions.find(td => td.name === task.taskName && task.taskGroup === td.groupName && (String(td.version) === String(task.taskVersion) || ignoreVersion)) || {})
    if(task.taskReadableName) {
      taskDef.definitionName = taskDef.readableName
      taskDef.readableName = task.taskReadableName
    }
    taskDef.definitionId = taskDef.id;
    // add here all incoming new task fields
    taskDef.isPaused = task.isPaused;
    (taskDef.params || []).forEach(param => {
      if (!param.dynamic) {
        if(param.valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN || param.valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN_PAUSE) {
          const val = task.taskOverrides.params[param.name]
          param.value = val !== undefined && val !== "" ? val  : (param.defaultValue !== undefined && param.defaultValue !== "" ? param.defaultValue : false);
          if(param.valueType === DataScienceEnums.TASK_PARAM_VALUE_TYPE.BOOLEAN_PAUSE && String(param.value) === "true") {
            taskDef.isPaused = true;
          }
        } else {
          param.value = resolveValue(task.taskOverrides.params[param.name], param.defaultValue)
        }
      } else if(task.taskOverrides.params[param.name] && !basicPipelineCreatorActions.isDynamicParameter(task.taskOverrides.params[param.name]) && String(task.taskOverrides.params[param.name]) !== "" && param.type === "INPUT") {
        param.value = resolveValue(task.taskOverrides.params[param.name], param.defaultValue)
      } else {
        param.value = cleanDynamicValue ? "" : task.taskOverrides.params[param.name]
      }
    })
    return taskDef
  }
  
  const getCommentNodes = (comments, removeComment, changeCommentPosition, diagram) => 
    comments.map(comment => {
      const commentNode = new CommentNodeModel(comment.id)
      let event = new Event('click');
      event = Object.assign(event, { clientX: comment.position.x, clientY: comment.position.y })
      const pointRel = diagram.getDiagramEngine().getRelativeMousePoint(event)
      commentNode.setPosition(pointRel);
      let timeout = null;
      commentNode.registerListener({
        entityRemoved: e => removeComment(e.entity.options.commentId),
        positionChanged: e => {
          clearTimeout(timeout)
          timeout = setTimeout(() => changeCommentPosition(e.entity.getPosition(), e.entity.options.commentId), 500)
        }
      })
      return commentNode;
    })
  
  const addOnlyConnectionPorts = (allLinks, paramsLinks) =>
    (allLinks || []).forEach(allLink => {
      const finded = paramsLinks.find(pL => pL.output.task === allLink.output && pL.input.task === allLink.input)
      if (!finded) {
        paramsLinks.push(getLinkObj(allLink.input, "OnlyConnectionInput", allLink.output, "OnlyConnectionOutput"))
      }
    })
  
  const getDiagramAndFilteredLinks = (paramsLinks, nodes, idField = "id") => {
    const diagramLinks = []
    paramsLinks = paramsLinks.map(pL => {
      const inputNode = nodes.find(node => node.getTask()[idField] === pL.input.task), 
            portInput = inputNode.ports[pL.input.name], 
            outputNode = nodes.find(node => node.getTask()[idField] === pL.output.task),
            portOutput = outputNode.ports[pL.output.name]
      if(!portOutput || !portInput) {
        return undefined;
      }
      const link = portOutput.link(portInput)
      diagramLinks.push(link)
      return Object.assign({}, pL, { id: link.options.id, input: { task: inputNode.getId(), name: pL.input.name }, output: { task: outputNode.getId(), name: pL.output.name } })
    }).filter(p => p !== undefined)
    return { diagramLinks, paramsLinks }
  }

  const getFirstPastePosition = (e, mouseX, mouseY, diagram) => {
    let newE;
    if(!e.clientX) {
      // pasted by shortcut
      newE = ReduxService.updateObject(encodeURI, { clientX: mouseX, clientY: mouseY })
    } else {
      // pasted by context menu
      newE = ReduxService.updateObject(e, { clientX: e.clientX, clientY: e.clientY - 96 }) // click is on the button in the menu
    }
    return diagram.getDiagramEngine().getRelativeMousePoint(newE)
  }

  const isNotATask = task => task.id === undefined  || task.category === undefined || task.groupName === undefined || task.name === undefined

  const cleanDynamicParams = task => {
    (task.params || []).forEach(p => {
      if(basicPipelineCreatorActions.isDynamicParameter(p?.value)) {
        p.value = ''  
      }
    })
  }

  const updateLinks = (links, oldTaskId, newTaskId) => {
    const linksWithTaskInOutput = links.filter(link => link.output.task === oldTaskId)
    linksWithTaskInOutput.forEach(link => link.output.task = newTaskId)
    const linksWithTaskInInput = links.filter(link => link.input.task === oldTaskId)
    linksWithTaskInInput.forEach(link => link.input.task = newTaskId)
  }

  const processPasteTasks = (paramTasks = [], paramLinks = [], e, getNode, mouseX, mouseY, diagram, categories, pipelineTasks) => {
    const tasks = paramTasks.sort((a, b) => b.position.x - a.position.x)
    const links = paramLinks.map(link => Object.assign({}, link, { oldValue: link.value, value: CommonService.generateUID() }))
    const firstPosition = getFirstPastePosition(e, mouseX, mouseY, diagram)
    const firstTaskPosition = tasks[0].position
    const nodes = []
    tasks.forEach((task, index) => {
      if(isNotATask(task)) {
        return; 
      }
      cleanDynamicParams(task)
      const newTaskId = CommonService.generateUID()
      updateLinks(links, task.id, newTaskId)
      task.id = newTaskId;
      task.readableName = getTasksNameInPipeline({ pipelineTasks, readableName: task.readableName, definitionName: task.definitionName || task.readableName })
      const position = index === 0 ? firstPosition : new Point(firstPosition.x - (firstTaskPosition.x - task.position.x), firstPosition.y - (firstTaskPosition.y - task.position.y))
      delete task.position
      const category = getCategory(task.category, categories),
            node = getNode(position, task, category, task.id)
      nodes.push(node)
    })
    const { diagramLinks, paramsLinks } = getDiagramAndFilteredLinks(links, nodes)
    return { nodes, diagramLinks, paramsLinks }
  }

  const getConnectingLinks = (links, tasks) => links.filter(link => tasks.findIndex(t => t.id === link.input.task) > -1 && tasks.findIndex(t => t.id === link.output.task) > -1)

  const getCopyContent = (builderNodes, selectedNodes, positions, links) => {
    const tasks = 
      Object.entries(builderNodes)
        .filter(([id, ]) => selectedNodes.includes(id)) // only selected nodes
        .map(([id, body]) => Object.assign({}, body, { position: positions[id], id })) // we have to save the position also

    const connectingLinks = getConnectingLinks(links, tasks)
    return { tasks, links: connectingLinks }
  }

  const prepareNode = (positions, task, taskDef, index, getNode, categories) => {
    const position = positions[task.taskId] || { x: 150 + 250 * index, y: 200 }
    const node = getNode(new Point(position.x, position.y), Object.assign({}, taskDef, { oldId: task.taskId, definitionName: taskDef.definitionName }), getCategory(taskDef.category, categories), task.taskId)
    return node;
  }

  const getMatchingTask = (tasks, task, taskDefinitions, value, param) => 
    tasks.filter(t => task.taskId !== t.taskId).find(t => {
      const definition = taskDefinitions.find(td => td.name === t.taskName && t.taskGroup === td.groupName && String(td.version) === String(t.taskVersion))
      return Object.entries(t.taskOverrides.params).findIndex(([k, v]) => v === value && definition.params.find(p => p.name === k)?.type !== param.type) > -1
    })

  const getParamsLink = (matchingTask, value, task, param) => {
    const key = Object.entries(matchingTask.taskOverrides.params).find(([, v]) => v === value)?.[0]
    return param.type === "OUTPUT" ? getLinkObj(matchingTask.taskId, key, task.taskId, param.name, value) : getLinkObj(task.taskId, param.name, matchingTask.taskId, key, value)
  }

  const getOnlyConnectionLinks = task => task.prevTasks.map(pT => ({ output: pT.id, input: task.taskId })).concat(task.nextTasks.map(nT => ({ input: nT.id, output: task.taskId })))

  const loadPipelineTasks = (tasks, taskDefinitions, positions, getNode, categories, { ignoreVersion } = {}) => {
    const nodes = []
    let allLinks = [], paramsLinks = []
    tasks.forEach((task, index) => {
      const taskDef = getTaskDef(taskDefinitions, task, { ignoreVersion })
      nodes.push(prepareNode(positions, task, taskDef, index, getNode, categories))
      const dynamicParams = taskDef.params.filter(p => p.dynamic) || []
      dynamicParams.forEach(param => {
        const value = task.taskOverrides.params[param.name]
        if (basicPipelineCreatorActions.isDynamicParameter(value)) {
          const matchingTask = getMatchingTask(tasks, task, taskDefinitions, value, param)
          if(!matchingTask) {
            param.value = value;
            return;
          }
          paramsLinks.push(getParamsLink(matchingTask, value, task, param))
        }
      })
      allLinks = allLinks.concat(getOnlyConnectionLinks(task))
    })
    paramsLinks = removeDuplicates(paramsLinks);
    return { paramsLinks, allLinks, nodes }
  }

  const attachNodeListeners = (node, { removeNode, setDoubleClickedTask, selectNodes, changePosition }) => {
    let timeout = undefined
    node.registerListener({
      entityRemoved: e => {
        removeNode(e.entity.options.id)
        setDoubleClickedTask(undefined)
        selectNodes(undefined)
      },
      positionChanged: e => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
          changePosition(
            e.entity.getId(),
            e.entity.getPosition()
          )
        }, 500)
      }
    })
  }

  return {
    getTask,
    getCommentNodes,
    getTaskDef,
    getCategory,
    getLinkObj,
    getDiagramAndFilteredLinks,
    addOnlyConnectionPorts,
    removeDuplicates,
    getFirstPastePosition,
    processPasteTasks,
    getCopyContent,
    loadPipelineTasks,
    getTasksNameInPipeline,
    attachNodeListeners
  }
})()
